// The admin-side phone-format rule (BL-1277), declared once and reused by every
// admin form schema that carries a phone field.
//
// Before this existed, every admin phone was `optionalFormString(50)` — a length
// cap and no format rule at all. An operator could type `201555`, submit, and the
// partial value persisted to `customers.phone_crm_not_for_notify_or_verify`, then
// rendered on the invoice Bill To panel as `+1201555`. The error-*rendering* path
// was already correct (ADR 016 `<FormField>` → `PhoneInput`); the missing piece
// was a rule that produces a message.
//
// The bidder side's equivalent lives in `@auctogon/core-bidder/schemas/profile`
// and refines via `normalizePhoneForCompare` (a structural E.164 shape check).
// The admin side is deliberately STRICTER — full `libphonenumber-js` `isValid()`
// via `validatePhone` — because every admin phone input is a `PhoneInput` with a
// country picker that assembles the country code for the operator, so a
// structurally-impossible number is a typo rather than an unusual-but-real
// number entered without its prefix.

import { z } from '@auctogon/core/zod';

import {
  isOnePhoneNumber,
  normalizePhoneForCompare,
} from '@auctogon/core/validation/phone';

/**
 * The operator-facing sentence, declared once.
 *
 * Deliberately a plain-English sentence rather than an error code: Zod messages
 * are what `parseBody` puts in the `fieldErrors` map and what `applyServerErrors`
 * renders, so an internal code used as a message IS the string the operator
 * reads (the BUG-1064 lesson, same as `FIRST_NAME_REQUIRED_MESSAGE`).
 */
export const PHONE_FORMAT_MESSAGE = 'Enter a valid phone number.';

/**
 * Whether two phone strings denote the same number.
 *
 * Raw equality is the primary test — an untouched field still holds exactly what
 * was loaded into it. The normalized comparison is the safety net for a
 * `PhoneInput` that reformats a stored value on mount (e.g. `2015550142` →
 * `+12015550142`): a cosmetic re-render must not read as an operator edit and
 * trip the grandfather clause. Junk values normalize to `null` on both sides, so
 * this correctly falls back to raw equality for exactly the legacy data the
 * grandfather clause exists to protect.
 */
function isSameNumber(a: string, b: string): boolean {
  if (a === b) return true;
  const normalizedA = normalizePhoneForCompare(a);
  return normalizedA !== null && normalizedA === normalizePhoneForCompare(b);
}

/** Inputs for {@link addPhoneFormatIssue} beyond the value and the Zod context. */
export interface PhoneFormatIssueOptions {
  /** Path the issue lands on, so `<FormField>` renders it under the right input. */
  path: (string | number)[];
  /**
   * The value currently persisted for this field, when the schema is validating
   * an edit of an existing record. Supplying it enables the grandfather clause
   * (BL-1277 decision 1b): a stored value the operator did not touch is accepted
   * unchanged, so a legacy partial phone cannot block an unrelated edit.
   *
   * MUST be read server-side from the database, never accepted from the client —
   * a client-supplied "original" would let any caller bypass the rule by echoing
   * back the junk it wanted to store.
   */
  persistedValue?: string | null;
}

/**
 * Add a phone-format issue to `ctx` when `value` is present, changed, and not a
 * valid phone number.
 *
 * Call this from a schema's `.superRefine(...)`, NOT from the object shape.
 * Zod skips `.refine`/`.superRefine` entirely when the object shape has already
 * failed, so a format rule living in the shape would suppress every sibling
 * cross-field error until the phone was fixed — an operator on an empty Business
 * form would fix errors one round at a time. That is precisely the BUG-1064
 * regression `addRequiredContactNameIssues` was written to prevent, and this
 * rule sits at the same refinement level for the same reason.
 *
 * Three values pass through untouched, in order:
 * - `undefined` — a patch-shaped schema's "leave this field unchanged"; nothing
 *   was submitted, so there is nothing to validate.
 * - `null` / `''` / whitespace — a deliberate clear. Phone is optional on every
 *   admin surface; the rule validates format only when a value is present.
 * - a value equal to `persistedValue` — the grandfather clause described above.
 *
 * @param value - The submitted phone, or null/undefined.
 * @param ctx - The Zod refinement context the issue is added to.
 * @param options - Issue path and the optional persisted value. See {@link PhoneFormatIssueOptions}.
 */
export function addPhoneFormatIssue(
  value: string | null | undefined,
  ctx: z.RefinementCtx,
  { path, persistedValue }: PhoneFormatIssueOptions,
): void {
  if (value === undefined) return;

  const submitted = (value ?? '').trim();
  if (submitted === '') return;

  const persisted = (persistedValue ?? '').trim();
  if (persisted !== '' && isSameNumber(submitted, persisted)) return;

  // BUG-2132: the whole value must be one number. `validatePhone` would pull a
  // number out of "call 218-555-0142" and pass it, and the value would then be
  // stored and shown as unlinked text.
  if (isOnePhoneNumber(submitted)) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: PHONE_FORMAT_MESSAGE,
    path,
  });
}
